import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Eye, Download, FileText, CheckCircle2, Clock, XCircle, FileSpreadsheet, Timer, AlertCircle } from "lucide-react";
import { motion } from "framer-motion";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import FileViewer from "../shared/FileViewer";
import { stripReferenciaDocumento } from "@/lib/utils";

export default function EditalCard({ edital, onView, isSelected, onToggleSelection }) {
  const showSelection = typeof onToggleSelection === "function";
  const statusConfig = {
    concluido: { 
      icon: CheckCircle2, 
      color: "bg-green-100 text-green-700 border-green-200",
      label: "Concluído"
    },
    concluido_parcial: {
      icon: AlertCircle,
      color: "bg-amber-100 text-amber-800 border-amber-200",
      label: "Parcial"
    },
    escaneado: {
      icon: FileText,
      color: "bg-slate-100 text-slate-700 border-slate-200",
      label: "Escaneado"
    },
    interrompido: {
      icon: Timer,
      color: "bg-orange-100 text-orange-800 border-orange-200",
      label: "Interrompido"
    },
    processando: { 
      icon: Clock, 
      color: "bg-amber-100 text-amber-700 border-amber-200",
      label: "Processando"
    },
    erro: { 
      icon: XCircle, 
      color: "bg-red-100 text-red-700 border-red-200",
      label: "Erro"
    }
  };

  // "processado" é legado da reextração de itens; tratar como concluído.
  const statusNorm = String(edital.status ?? "").trim().toLowerCase();
  const statusEfetivo = statusNorm === "processado" ? "concluido" : statusNorm;
  const statusMeta = statusConfig[statusEfetivo];
  const StatusIcon = statusMeta?.icon || AlertCircle;
  const statusLabel = statusMeta?.label || (statusEfetivo ? statusEfetivo : "Sem status");
  /** O card já é o processo. A tela de resultado abre em qualquer status. */
  const podeVisualizar = true;
  const podeExportar =
    statusEfetivo === "concluido" ||
    statusEfetivo === "concluido_parcial" ||
    statusEfetivo === "escaneado" ||
    statusEfetivo === "interrompido";



  const formatarDuracaoProcessamento = (ms) => {
    if (typeof ms !== "number" || !Number.isFinite(ms) || ms < 0) return null;
    const totalSec = Math.round(ms / 1000);
    const mins = Math.floor(totalSec / 60);
    const secs = totalSec % 60;
    if (mins === 0) return `${secs}s`;
    if (secs === 0) return `${mins} min`;
    return `${mins} min ${secs}s`;
  };

  const duracaoProcessamento = formatarDuracaoProcessamento(
    edital.processamento_metricas?.duracao_ms
  );

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return '';
      }
      return format(date, "d 'de' MMMM 'de' yyyy, HH:mm", { locale: ptBR });
    } catch (e) {
      console.error("Error formatting date:", e);
      return '';
    }
  };

  const dataFormatada = formatDate(edital.created_date);
  const ehNaoLocalizado = (v) => !v || (typeof v === 'string' && /^n[ãa]o localizado(\s+no edital)?$/i.test(v.trim()));
  const ehTituloFragmentado = (title) => {
    if (!title || typeof title !== "string") return true;
    const t = title.trim();
    if (t.length < 15) return true;
    return /^(ou|e|de|da|do|das|dos|em|no|na|nos|nas)\s/i.test(t);
  };
  const rawTitle =
    (!ehNaoLocalizado(edital.titulo_licitacao) && edital.titulo_licitacao) ||
    (!ehNaoLocalizado(edital.instituicao) && edital.instituicao) ||
    edital.nome_arquivo ||
    edital.titulo_licitacao ||
    edital.instituicao ||
    "";
  let displayTitle =
    (typeof rawTitle === "string"
      ? stripReferenciaDocumento(rawTitle).trim()
      : "") || "Sem título";
  if (ehTituloFragmentado(displayTitle) && edital.nome_arquivo) {
    displayTitle = edital.nome_arquivo;
  }

  const processoExibir =
    edital.numero_processo && edital.numero_processo !== "Não localizado"
      ? stripReferenciaDocumento(edital.numero_processo)
      : null;
  const objetoExibir =
    edital.objeto && edital.objeto !== "Não localizado"
      ? stripReferenciaDocumento(edital.objeto)
      : null;

  const exportQuick = () => {
    const rows = [];
    rows.push(['Campo', 'Valor']);
    rows.push(['Processo', processoExibir || edital.numero_processo || '']);
    rows.push(['Comissão', edital.comissao_licitacao || '']);
    rows.push(['Data Abertura', edital.data_abertura || '']);
    rows.push(['Modalidade', edital.modalidade || '']);
    rows.push(['Objeto', objetoExibir || edital.objeto || '']);
    rows.push(['Confiança Média', edital.confianca_media && typeof edital.confianca_media === 'number' ? `${edital.confianca_media.toFixed(1)}%` : '']);

    const csvContent = rows.map(row => 
      row.map(cell => `"${String(cell || '').replace(/"/g, '""')}"`).join(',')
    ).join('\n');

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `resumo_${edital.numero_processo || 'edital'}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <Card className={`border-none shadow-md hover:shadow-xl transition-all duration-300 bg-white/90 backdrop-blur-sm overflow-hidden ${showSelection && isSelected ? 'ring-2 ring-blue-500' : ''}`}>
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row gap-6 min-w-0">
            {showSelection && (
              <div className="flex items-start gap-3">
                <Checkbox
                  checked={!!isSelected}
                  onCheckedChange={() => onToggleSelection(edital.id)}
                  className="mt-1"
                />
              </div>
            )}

            <div className="flex-1 min-w-0">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <FileText className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-lg text-slate-900 mb-1 line-clamp-2 break-words">
                    {displayTitle}
                  </h3>
                  {dataFormatada && (
                    <p className="text-sm text-slate-600">
                      {dataFormatada}
                    </p>
                  )}
                </div>
              </div>

              {processoExibir && (
                <div className="mb-2 min-w-0">
                  <span className="text-sm text-slate-600">Processo: </span>
                  <span className="text-sm font-medium text-slate-900 break-words">{processoExibir}</span>
                </div>
              )}

              {objetoExibir && (
                <p className="text-sm text-slate-600 line-clamp-2 break-words mb-3">
                  {objetoExibir}
                </p>
              )}

              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="outline" className={`${statusConfig[statusEfetivo]?.color || "bg-slate-100 text-slate-700 border-slate-200"} border flex items-center gap-1`}>
                  <StatusIcon className="w-3 h-3" />
                  {statusLabel}
                </Badge>
                {podeExportar && duracaoProcessamento && (
                  <span className="inline-flex items-center gap-1 text-sm text-slate-600">
                    <Timer className="w-3.5 h-3.5" />
                    Processado em {duracaoProcessamento}
                  </span>
                )}
              </div>
            </div>

            <div className="flex flex-col gap-2 shrink-0 w-full md:w-44 min-w-0">
              <Button
                onClick={() => onView(edital)}
                className="w-full bg-blue-600 hover:bg-blue-700"
                disabled={!podeVisualizar}
              >
                <Eye className="w-4 h-4 mr-2" />
                Visualizar
              </Button>
              
              {podeExportar && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="w-full">
                      <Download className="w-4 h-4 mr-2" />
                      Exportar
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem onClick={exportQuick}>
                      <FileSpreadsheet className="w-4 h-4 mr-2" />
                      Resumo CSV
                    </DropdownMenuItem>
                    {edital.arquivo_original && (
                      <DropdownMenuItem asChild>
                        <div className="w-full">
                          <FileViewer
                            fileUrl={edital.arquivo_original}
                            fileName={edital.nome_arquivo || 'Edital Original'}
                            trigger={
                              <div className="flex items-center cursor-pointer w-full">
                                <FileText className="w-4 h-4 mr-2" />
                                Original
                              </div>
                            }
                          />
                        </div>
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              
              {edital.arquivo_original && !podeExportar && (
                <FileViewer
                  fileUrl={edital.arquivo_original}
                  fileName={edital.nome_arquivo || 'Edital Original'}
                  trigger={
                    <Button variant="outline" className="w-full">
                      <Download className="w-4 h-4 mr-2" />
                      Original
                    </Button>
                  }
                />
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}